import React, { useState } from "react";
import { Sparkles, Loader2, Check, SpellCheck, MessageSquare } from "lucide-react";

import { Button } from "../ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { cn } from "../../lib/utils";
import api from "../../services/api";
import toastError from "../../errors/toastError";

// Ajuda de IA para quem está digitando.
//
// São duas coisas diferentes, e quem escolhe é o atendente: corrigir o que ele
// escreveu, ou sugerir o que responder a partir da conversa. Antes o quadro
// fazia as duas de uma vez — duas chamadas pagas toda vez que abria, sendo que
// quem quer corrigir não quer sugestão nenhuma.
//
// Nada sai sozinho: escolher uma opção só preenche o campo. Quem envia é a
// pessoa, depois de ler.

const TONS = [
  { acao: "corrigir", rotulo: "Corrigido" },
  { acao: "melhorar", rotulo: "Mais claro" },
  { acao: "formal", rotulo: "Mais formal" },
  { acao: "informal", rotulo: "Mais informal" },
  { acao: "resumir", rotulo: "Mais curto" },
];

const Cartao = ({ titulo, texto, aoUsar }) => (
  <button
    type="button"
    onClick={aoUsar}
    className="group w-full rounded-lg border bg-card p-3 text-left transition-colors hover:border-primary/50 hover:bg-accent"
  >
    <div className="flex items-center justify-between gap-2 pb-1">
      <span className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
        {titulo}
      </span>
      <span className="flex items-center gap-1 text-[11px] text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100">
        <Check className="h-3 w-3" /> usar
      </span>
    </div>
    <p className="whitespace-pre-wrap text-sm leading-relaxed">{texto}</p>
  </button>
);

const AssistenteDeTexto = ({ ticketId, texto, aoTrocarTexto, desabilitado }) => {
  const [aberto, setAberto] = useState(false);
  const [modo, setModo] = useState(null);
  const [carregando, setCarregando] = useState("");
  const [erro, setErro] = useState("");

  // Uma entrada por versão gerada: cada uma vira um cartão próprio.
  const [versoes, setVersoes] = useState([]);
  const [sugestoes, setSugestoes] = useState([]);

  const digitado = String(texto || "").trim();
  const temTexto = Boolean(digitado);

  const abrir = () => {
    setVersoes([]);
    setSugestoes([]);
    setErro("");
    // Sem nada digitado não há o que corrigir: já abre em sugestões.
    setModo(temTexto ? "corrigir" : "sugerir");
    setAberto(true);

    if (temTexto) gerarVersao("corrigir", "Corrigido");
    else buscarSugestoes();
  };

  const trocarModo = (novo) => {
    setModo(novo);
    setErro("");

    if (novo === "sugerir" && !sugestoes.length) buscarSugestoes();
    if (novo === "corrigir" && !versoes.length) gerarVersao("corrigir", "Corrigido");
  };

  const gerarVersao = async (acao, rotulo) => {
    if (carregando) return;

    setCarregando(acao);
    setErro("");

    try {
      const { data } = await api.post("/ai/improve", {
        text: digitado,
        action: acao,
      });

      const resultado = String(data?.texto || "").trim();

      if (!resultado) {
        setErro("A IA não devolveu texto.");
        return;
      }

      setVersoes((atuais) => [
        ...atuais.filter((v) => v.acao !== acao),
        { acao, rotulo, texto: resultado },
      ]);
    } catch (err) {
      // O backend recusa resposta que não é reescrita — modelo pequeno que
      // devolve o próprio raciocínio cai aqui, e é melhor dizer isso do que
      // oferecer aquilo como mensagem pronta.
      setErro(
        "A IA não devolveu uma correção utilizável. Tente de novo ou troque o modelo em Configurações › Inteligência Artificial."
      );
    } finally {
      setCarregando("");
    }
  };

  const buscarSugestoes = async () => {
    if (carregando) return;

    setCarregando("sugerir");
    setErro("");

    try {
      const { data } = await api.post(`/tickets/${ticketId}/ai/suggest`, {
        instruction: temTexto ? `Continue a partir de: ${digitado}` : undefined,
      });

      const lista = (data?.sugestoes || []).filter(Boolean);

      setSugestoes(lista);

      if (!lista.length) setErro("A IA não sugeriu nada desta vez.");
    } catch (err) {
      setErro("Não consegui falar com a IA.");
      toastError(err);
    } finally {
      setCarregando("");
    }
  };

  const usar = (novoTexto) => {
    aoTrocarTexto(novoTexto);
    setAberto(false);
  };

  const Aba = ({ valor, icone: Icone, rotulo, desativada }) => (
    <button
      type="button"
      onClick={() => trocarModo(valor)}
      disabled={desativada}
      className={cn(
        "flex flex-1 items-center justify-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium transition-colors",
        modo === valor
          ? "bg-background shadow-sm"
          : "text-muted-foreground hover:text-foreground",
        desativada && "cursor-not-allowed opacity-50"
      )}
    >
      <Icone className="h-3.5 w-3.5" />
      {rotulo}
    </button>
  );

  return (
    <>
      <Button
        type="button"
        variant="ghost"
        size="icon"
        className="shrink-0"
        aria-label="Corrigir e sugerir com IA"
        title="Corrigir & sugestão"
        onClick={abrir}
        disabled={desabilitado}
      >
        <Sparkles className="h-5 w-5 text-muted-foreground" />
      </Button>

      <Dialog open={aberto} onOpenChange={setAberto}>
        <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Corrigir & sugestão</DialogTitle>
          </DialogHeader>

          <div className="flex gap-1 rounded-lg bg-muted p-1">
            <Aba
              valor="corrigir"
              icone={SpellCheck}
              rotulo="Corrigir meu texto"
              desativada={!temTexto}
            />
            <Aba valor="sugerir" icone={MessageSquare} rotulo="Sugerir resposta" />
          </div>

          <p className="text-xs text-muted-foreground">
            {modo === "corrigir"
              ? "Toque na versão que quiser usar. Ela vai para o campo de mensagem."
              : "A IA lê a conversa e propõe respostas. Nada é enviado sem você conferir."}
          </p>

          {erro && <p className="text-sm text-destructive">{erro}</p>}

          {modo === "corrigir" ? (
            <div className="space-y-2">
              {versoes.map((v) => (
                <Cartao
                  key={v.acao}
                  titulo={v.rotulo}
                  texto={v.texto}
                  aoUsar={() => usar(v.texto)}
                />
              ))}

              {carregando && carregando !== "sugerir" && (
                <div className="flex items-center gap-2 py-3 text-sm text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Revisando o texto…
                </div>
              )}

              <div className="flex flex-wrap gap-1.5 pt-0.5">
                {TONS.filter(
                  (t) => !versoes.some((v) => v.acao === t.acao)
                ).map((t) => (
                  <Button
                    key={t.acao}
                    type="button"
                    variant="outline"
                    size="sm"
                    className="h-7 text-xs"
                    onClick={() => gerarVersao(t.acao, t.rotulo)}
                    disabled={Boolean(carregando)}
                  >
                    {carregando === t.acao && (
                      <Loader2 className="h-3 w-3 animate-spin" />
                    )}
                    {t.rotulo}
                  </Button>
                ))}
              </div>
            </div>
          ) : (
            <div className="space-y-2">
              {carregando === "sugerir" ? (
                <div className="flex items-center gap-2 py-3 text-sm text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Lendo a conversa…
                </div>
              ) : (
                sugestoes.map((sugestao, i) => (
                  <Cartao
                    key={i}
                    titulo={`Sugestão ${i + 1}`}
                    texto={sugestao}
                    aoUsar={() => usar(sugestao)}
                  />
                ))
              )}

              {!carregando && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="h-7 w-full text-xs"
                  onClick={buscarSugestoes}
                >
                  {sugestoes.length ? "Gerar outras" : "Tentar de novo"}
                </Button>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default AssistenteDeTexto;
